import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { aboutInfo } from '@/mocks/about';
import { useState } from 'react';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于爱液视频',
  description: aboutInfo.description,
  url: 'https://aiyeshipin.com/about',
  about: {
    '@type': 'Organization',
    name: aboutInfo.brandName,
    description: aboutInfo.description,
    foundingDate: aboutInfo.foundedYear,
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '深圳',
      addressRegion: '广东',
      addressCountry: 'CN',
    },
  },
};

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reviews' | 'contact'>('intro');

  return (
    <>
      <SEOMeta
        title="关于爱液视频 - 品牌介绍、口碑评价与联系方式 | 爱液视频App"
        description="爱液视频成立于2019年，专注于视频内容聚合与分发，累计服务超过5000万用户。了解我们的品牌故事、核心价值观、发展历程、用户口碑和联系方式。"
        keywords="爱液视频app,关于爱液视频,爱液视频品牌,爱液视频联系方式,爱液视频口碑"
        canonicalUrl="https://aiyeshipin.com/about"
      />
      <JsonLd data={aboutJsonLd} />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于爱液视频
            </h1>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto">
              {aboutInfo.description}
            </p>
          </div>

          <div className="flex items-center justify-center mb-10">
            <div className="inline-flex bg-background-100 rounded-full p-1">
              {([
                { key: 'intro', label: '品牌介绍', icon: 'ri-building-line' },
                { key: 'reviews', label: '用户口碑', icon: 'ri-chat-heart-line' },
                { key: 'contact', label: '联系我们', icon: 'ri-customer-service-2-line' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    activeTab === tab.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'intro' && (
            <div className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-4">
                    品牌使命
                  </h2>
                  <p className="text-foreground-600 text-base leading-relaxed mb-8">
                    {aboutInfo.mission}
                  </p>
                  <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-4">
                    品牌愿景
                  </h2>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {aboutInfo.vision}
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-4">
                    基本信息
                  </h2>
                  <dl className="flex flex-col gap-4">
                    <div className="flex justify-between py-2 border-b border-background-200">
                      <dt className="text-foreground-500 text-sm">公司名称</dt>
                      <dd className="text-foreground-950 text-sm font-medium">{aboutInfo.brandName}</dd>
                    </div>
                    <div className="flex justify-between py-2 border-b border-background-200">
                      <dt className="text-foreground-500 text-sm">成立时间</dt>
                      <dd className="text-foreground-950 text-sm font-medium">{aboutInfo.foundedYear}</dd>
                    </div>
                    <div className="flex justify-between py-2 border-b border-background-200">
                      <dt className="text-foreground-500 text-sm">总部地址</dt>
                      <dd className="text-foreground-950 text-sm font-medium">{aboutInfo.headquarters}</dd>
                    </div>
                    <div className="flex justify-between py-2">
                      <dt className="text-foreground-500 text-sm">团队规模</dt>
                      <dd className="text-foreground-950 text-sm font-medium">{aboutInfo.employees}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-6 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
                {aboutInfo.values.map((val, index) => (
                  <div
                    key={val.title}
                    className="bg-background-100 rounded-xl p-6 text-center animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <span className="text-primary-600 text-lg font-heading font-bold">{index + 1}</span>
                    </div>
                    <h3 className="text-foreground-950 font-heading font-semibold text-sm mb-2">
                      {val.title}
                    </h3>
                    <p className="text-foreground-500 text-xs">{val.description}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-6 text-center">
                发展历程
              </h2>
              <div className="relative max-w-3xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="flex flex-col gap-6">
                  {aboutInfo.milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex items-start gap-6 md:gap-10 animate-fade-in-up ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="absolute left-2 md:left-1/2 w-5 h-5 bg-primary-500 rounded-full border-4 border-background-50 z-10 md:-translate-x-2.5"></div>
                      <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full mb-2">
                          {item.year}
                        </span>
                        <p className="text-foreground-600 text-sm leading-relaxed">{item.event}</p>
                      </div>
                      {index % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : <div className="hidden md:block md:w-1/2"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-lg"></i>
                  ))}
                </div>
                <p className="text-foreground-500 text-sm">综合评分 4.7 · 基于 {aboutInfo.reviews.length}+ 条用户评价</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutInfo.reviews.map((review, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-xl p-5 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full font-heading font-semibold text-sm">
                        {review.avatar}
                      </div>
                      <div>
                        <p className="text-foreground-950 font-medium text-sm">{review.name}</p>
                        <p className="text-foreground-400 text-xs">{review.date}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed">{review.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="animate-fade-in max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-foreground-950 font-heading font-semibold text-sm mb-1">电子邮件</h3>
                  <a
                    href={`mailto:${aboutInfo.contact.email}`}
                    className="text-primary-600 text-sm hover:underline cursor-pointer"
                  >
                    {aboutInfo.contact.email}
                  </a>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-phone-line text-accent-700 text-xl"></i>
                  </div>
                  <h3 className="text-foreground-950 font-heading font-semibold text-sm mb-1">客服电话</h3>
                  <p className="text-foreground-600 text-sm">{aboutInfo.contact.phone}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-time-line text-secondary-700 text-xl"></i>
                  </div>
                  <h3 className="text-foreground-950 font-heading font-semibold text-sm mb-1">工作时间</h3>
                  <p className="text-foreground-600 text-sm">{aboutInfo.contact.workHours}</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 mb-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  <i className="ri-map-pin-line mr-2 text-primary-600"></i>
                  公司地址
                </h2>
                <p className="text-foreground-600 text-sm">{aboutInfo.contact.address}</p>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  <i className="ri-share-line mr-2 text-primary-600"></i>
                  关注我们
                </h2>
                <div className="flex flex-wrap gap-3">
                  {aboutInfo.contact.socialMedia.map((social) => (
                    <a
                      key={social.name}
                      href={social.link}
                      className="flex items-center gap-2 px-4 py-2.5 bg-background-50 rounded-lg text-foreground-600 text-sm hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer"
                      rel="nofollow"
                    >
                      <i className={`${social.icon} text-lg`}></i>
                      {social.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}